import React, { useEffect, useState } from 'react';

import { Autocomplete, createFilterOptions, FilterOptionsState, Popper } from '@mui/material';
import clsx from 'clsx';

import Checkbox from 'components/common/Checkbox/Checkbox';

import { ClearIcon } from '../Icons/Icons';
import styles from './MultipleSelect.module.scss';
import { AutoCompleteInput, AutoCompleteLabel } from './StyledComponents';
import {
  DEFAULT_LIMIT_TAGS_VALUE,
  DEFAULT_SELECT_ALL_OPTION_LABEL,
  DEFAULT_SELECT_ALL_OPTION_VALUE,
} from './multipleSelect.constants';
import { ArrowDownIcon } from '../Icons/Icons';

export interface MultipleSelectOption {
  label: string;
  value: string;
}

interface MultipleSelectProps {
  id: string;
  selectOptions: MultipleSelectOption[];
  inputLabel: string;
  useSelectAll: boolean;
  disabled: boolean;
  onChange: (value: MultipleSelectOption[]) => void;
  limitTags?: number;
  selectAllLabel?: string;
  errorMessage?: string | undefined;
  initialValues?: MultipleSelectOption[];
  useRequiredSymbol?: boolean;
}

const MultipleSelect = ({
  id,
  selectOptions,
  onChange,
  useSelectAll,
  disabled,
  inputLabel,
  errorMessage,
  selectAllLabel = DEFAULT_SELECT_ALL_OPTION_LABEL,
  limitTags = DEFAULT_LIMIT_TAGS_VALUE,
  initialValues = [],
  useRequiredSymbol = false,
}: MultipleSelectProps) => {
  const [currentValues, setCurrentValues] = useState<MultipleSelectOption[]>(initialValues);
  const scrollTopRef = React.useRef(0);

  useEffect(() => {
    currentValues.forEach((value) => {
      if (!selectOptions.find((option) => option.label === value.label)) {
        setCurrentValues([]);
      }
    });
  }, [currentValues, selectOptions]);

  useEffect(() => {
    setCurrentValues(initialValues);
  }, [initialValues]);

  return (
    <div className={styles.mainContainer}>
      <Autocomplete
        id={id}
        sx={{
          height: 'auto',
          '& .MuiTypography-root': {
            fontSize: '16px',
            lineHeight: '24px',
            fontWeight: 'normal',
            fontFamily: 'Poppins',
          },
          '& .MuiInputBase-input': {
            padding: '16px 20px',
          },
          '& .MuiInputBase-root.Mui-disabled': {
            background: '#f4f5f6',
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: '#687a8c',
            },
          },
          '&.error-autocomplete .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderColor: '#ff2e00',
          },
          '& .MuiChip-root': { maxWidth: '200px' },
          '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
        }}
        multiple
        className={errorMessage ? 'error-autocomplete' : ''}
        options={selectOptions}
        value={currentValues}
        popupIcon={<ArrowDownIcon />}
        disabled={disabled}
        disableCloseOnSelect
        getOptionLabel={(option) => option.label}
        PopperComponent={({ style, ...props }) => <Popper {...props} style={{ ...style, height: 0 }} />}
        filterOptions={(options, params) => {
          const filter = createFilterOptions();
          const filteredOptions = filter(options, params as FilterOptionsState<unknown>);

          if (useSelectAll) {
            return [
              { label: selectAllLabel, value: DEFAULT_SELECT_ALL_OPTION_VALUE },
              ...filteredOptions,
            ] as unknown as MultipleSelectOption[];
          } else {
            return filteredOptions as unknown as MultipleSelectOption[];
          }
        }}
        renderTags={(options, getTagProps) => (
          <>
            {
              <>
                {options.slice(0, limitTags).map((option, index) => (
                  <AutoCompleteLabel
                    label={option.label}
                    deleteIcon={<ClearIcon />}
                    {...getTagProps({ index: index })}
                  />
                ))}
                <div>{options.length > limitTags && ` +${options.length - limitTags}`}</div>
              </>
            }
          </>
        )}
        renderOption={(props, option, { selected }) => (
          <li {...props} className={clsx(props.className, selected && styles.selectedOption)}>
            <Checkbox
              name="selectItem"
              isChecked={
                option.value === DEFAULT_SELECT_ALL_OPTION_VALUE
                  ? currentValues.length === selectOptions.length
                  : selected
              }
              onChange={() => {}}
            />
            {option.label}
          </li>
        )}
        renderInput={(param) => (
          <>
            <div className={styles.inputLabel}>
              {inputLabel} {useRequiredSymbol && <div className={styles.requiredInputLabel}>*</div>}
            </div>
            <AutoCompleteInput isMobile={false} errorMessage={errorMessage} {...param} />
          </>
        )}
        onChange={(_event, options) => {
          if (options.find((option) => option.value === DEFAULT_SELECT_ALL_OPTION_VALUE)) {
            onChange(currentValues.length === selectOptions.length ? [] : selectOptions);
            setCurrentValues(currentValues.length === selectOptions.length ? [] : selectOptions);
          } else {
            onChange(options);
            setCurrentValues(options);
          }
        }}
        ListboxProps={{
          onScroll: (event: React.UIEvent<HTMLUListElement>) => {
            scrollTopRef.current = event.currentTarget.scrollTop;
          },
          ref: (node: HTMLUListElement) => {
            if (node) {
              node.scrollTop = scrollTopRef.current;
            }
          },
        }}
      />
      {!!errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
    </div>
  );
};

export default MultipleSelect;
